import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import {
  BulkMoveToTrashMediaHouseDocument,
  type BulkMoveToTrashMediaHouseMutation,
  type BulkMoveToTrashMediaHouseMutationVariables,
  GetMediaHouseAudioDocument,
  type MediaEdge,
  type MediaHouseMedia,
} from "@/generated/graphql";
import { Dnd } from "@/lib/dnd/components/Dnd";
import { useMediaHandler } from "@/lib/hooks/slides/editor/useMediaHandler";
import { Button } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiAlertTriangle,
  FiCheck,
  FiMusic,
  FiPause,
  FiPlay,
  FiRefreshCw,
  FiUploadCloud,
  FiZap,
} from "react-icons/fi";
import { DisabledLockOverlay } from "../DisabledLockOverlay";
import { MediaPropertiesDialog } from "./modals/MediaPropertiesDialog";
import { MediaUploadModal } from "./modals/MediaUploadModal";
import { MoveMediaModal } from "./modals/MoveMediaModal";
import { RenameMediaModal } from "./modals/RenameMediaModal";
import { TextToSpeechModal } from "./modals/TextToSpeechModal";
import { FoldersSection } from "./sections/FoldersSection";
import { FloatingActionBar } from "./shared/FloatingActionBar";
import { ThreeDotMenu } from "./shared/ThreeDotMenu";

const isServerError = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const error = err as { networkError?: unknown; graphQLErrors?: unknown[] };
  if (error.networkError) return true;
  const serverErrorCodes = ["INTERNAL_SERVER_ERROR", "SERVICE_UNAVAILABLE"];
  return (
    error.graphQLErrors?.some((e: unknown) => {
      const graphQLError = e as { extensions?: { code?: string } };
      return serverErrorCodes.includes(graphQLError.extensions?.code as string);
    }) ?? false
  );
};

interface AudioTabProps {
  disabled?: boolean;
}

export function AudioTab({ disabled = false }: AudioTabProps) {
  const { setActivePanel } = useSlideEditorContext();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const isScrubbing = useRef(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<Map<string, MediaHouseMedia>>(new Map());
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [audioTimes, setAudioTimes] = useState<Map<string, { current: number; duration: number }>>(
    new Map()
  );

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showPropertiesDialog, setShowPropertiesDialog] = useState(false);
  const [showTextToSpeechModal, setShowTextToSpeechModal] = useState(false);
  const [activeMedia, setActiveMedia] = useState<MediaHouseMedia | null>(null);
  const [itemsToMove, setItemsToMove] = useState<MediaHouseMedia[]>([]);

  const observerTarget = useRef<HTMLDivElement>(null);
  const { addAudioToSlide, copying } = useMediaHandler();

  const { data, loading, error, fetchMore, refetch } = useQuery(GetMediaHouseAudioDocument, {
    variables: { folderId: currentFolderId, first: 20 },
    fetchPolicy: "cache-and-network",
    skip: disabled,
  });

  const [bulkDelete] = useMutation<
    BulkMoveToTrashMediaHouseMutation,
    BulkMoveToTrashMediaHouseMutationVariables
  >(BulkMoveToTrashMediaHouseDocument, {
    refetchQueries: ["GetMediaHouseAudio"],
    onCompleted: (data) => {
      toast.success(
        `Moved ${data.bulkMoveToTrashMediaHouse} ${data.bulkMoveToTrashMediaHouse === 1 ? "item" : "items"} to trash`
      );
      setSelectedMedia(new Map());
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleAudioClick = async (mediaHouseMedia: MediaHouseMedia) => {
    await addAudioToSlide(mediaHouseMedia.id);
    setActivePanel(null);
  };

  const togglePlayPause = (e: React.MouseEvent, audioId: string) => {
    e.stopPropagation();
    const audioEl = audioRefs.current.get(audioId);
    if (!audioEl) return;
    if (playingId === audioId) {
      audioEl.pause();
      setPlayingId(null);
    } else {
      audioRefs.current.forEach((a, id) => {
        if (id !== audioId) a.pause();
      });
      audioEl.play();
      setPlayingId(audioId);
    }
  };

  const handleToggleSelect = (id: string, media: MediaHouseMedia) => {
    setSelectedMedia((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, media);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedMedia(new Map());
  const handleRename = (item: MediaHouseMedia) => {
    setActiveMedia(item);
    setShowRenameModal(true);
  };
  const handleMove = (items: MediaHouseMedia[]) => {
    setItemsToMove(items);
    setShowMoveModal(true);
  };
  const handleProperties = (item: MediaHouseMedia) => {
    setActiveMedia(item);
    setShowPropertiesDialog(true);
  };
  const handleDelete = async (items: MediaHouseMedia[]) => {
    if (!confirm(`Move ${items.length} ${items.length === 1 ? "item" : "items"} to trash?`)) return;
    await bulkDelete({ variables: { ids: items.map((item) => item.id) } });
  };

  const selectedMediaArray = Array.from(selectedMedia.values());
  const selectedIds = new Set(selectedMedia.keys());
  const audioFiles = data?.mediaHouseMedia.edges || [];
  const sortedAudios = [...audioFiles].sort(
    (a, b) => new Date(b.node.createdAt).getTime() - new Date(a.node.createdAt).getTime()
  );
  const hasSelection = selectedMedia.size > 0;
  const pageInfo = data?.mediaHouseMedia?.pageInfo;
  const hasMore = pageInfo?.hasNextPage || false;

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target?.isIntersecting && hasMore && !loading) {
        fetchMore({ variables: { after: pageInfo?.endCursor } });
      }
    },
    [hasMore, loading, fetchMore, pageInfo?.endCursor]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div className="pt-2 pb-3 relative">
      <FoldersSection
        parentId={currentFolderId}
        mediaType="audio"
        onFolderClick={setCurrentFolderId}
        disabled={disabled}
      />

      <div
        className={`max-h-[calc(100vh-300px)] overflow-y-auto pr-0.5 gradient-scrollbar ${
          disabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
      >
        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          <Button
            onClick={() => setShowTextToSpeechModal(true)}
            variant="primary"
            size="sm"
            disabled={disabled}
            leftIcon={<FiZap className="h-3.5 w-3.5" />}
            className="flex-1"
          >
            Generate
          </Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="secondary"
            size="sm"
            disabled={disabled}
            leftIcon={<FiUploadCloud className="h-3.5 w-3.5" />}
            className="flex-1"
          >
            Upload
          </Button>
        </div>

        {/* Audio List */}
        <div className="space-y-1.5">
          {loading && audioFiles.length === 0 ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton tiles
                key={i}
                className="h-[60px] rounded-xl bg-slate-100 dark:bg-gray-800 animate-pulse"
                style={{ animationDelay: `${i * 80}ms` }}
              />
            ))
          ) : error ? (
            <div
              className="flex flex-col items-center justify-center py-10 px-4 gap-3"
              role="alert"
            >
              <div className="w-12 h-12 rounded-2xl bg-error-50 dark:bg-error-900/30 flex items-center justify-center">
                <FiAlertTriangle className="text-lg text-error-500" />
              </div>
              <p className="text-xs font-semibold text-slate-600 dark:text-gray-300">
                {isServerError(error) ? "Media House unavailable" : "Failed to load"}
              </p>
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                {isServerError(error) ? "The server appears to be down." : error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="primary"
                size="sm"
                leftIcon={<FiRefreshCw className="h-3 w-3" />}
              >
                Retry
              </Button>
            </div>
          ) : audioFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(20,184,166,0.12) 100%)",
                }}
              >
                <FiMusic className="text-2xl text-slate-400 dark:text-gray-500" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-gray-400">
                No audio yet
              </p>
              <p className="text-[11px] text-slate-400 dark:text-gray-500 text-center leading-relaxed max-w-[160px]">
                Upload an audio file or generate one with AI
              </p>
            </div>
          ) : (
            sortedAudios.map(({ node: audio }: MediaEdge) => {
              const isSelected = selectedIds.has(audio.id);
              const isHovered = hoveredId === audio.id;
              const isPlaying = playingId === audio.id;
              const times = audioTimes.get(audio.id) || { current: 0, duration: 0 };
              const percent = times.duration ? (times.current / times.duration) * 100 : 0;
              const showOverlay = isHovered || isSelected || hasSelection;

              return (
                <Dnd
                  id={audio.id}
                  key={audio.id}
                  data={{
                    id: audio.id,
                    type: "audio",
                    media: { src: audio.url, filename: audio.filename },
                  }}
                >
                  <div
                    className={`group relative flex items-center gap-2.5 px-2 py-2 rounded-xl border transition-all duration-150 cursor-pointer ${
                      isSelected
                        ? "border-primary-400 dark:border-primary-600 bg-primary-50/60 dark:bg-primary-900/20 shadow-sm shadow-primary-500/15"
                        : "border-slate-200 dark:border-gray-700/60 bg-slate-50 dark:bg-gray-800/60 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 hover:shadow-sm"
                    }`}
                    onMouseEnter={() => setHoveredId(audio.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => {
                      // If they were just scrubbing the timeline, ignore the click!
                      if (isScrubbing.current) return;

                      if (!copying && !isSelected) handleAudioClick(audio);
                    }}
                  >
                    {/* Hidden Audio Element */}
                    <audio
                      ref={(el) => {
                        if (el && !audioRefs.current.has(audio.id)) {
                          audioRefs.current.set(audio.id, el);
                          const update = () =>
                            setAudioTimes((prev) =>
                              new Map(prev).set(audio.id, {
                                current: el.currentTime,
                                duration: el.duration || 0,
                              })
                            );
                          el.addEventListener("timeupdate", update);
                          el.addEventListener("loadedmetadata", update);
                        }
                      }}
                      src={audio.url}
                      onPlay={() => setPlayingId(audio.id)}
                      onPause={() => setPlayingId(null)}
                      onEnded={(e) => {
                        setPlayingId(null);
                        e.currentTarget.currentTime = 0;
                      }}
                    >
                      <track kind="captions" />
                    </audio>

                    {/* Checkbox or Play button */}
                    <div
                      className="flex items-center gap-2 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className={`transition-opacity duration-150 flex items-center justify-center ${
                          showOverlay ? "opacity-100" : "opacity-0 pointer-events-none"
                        } ${hasSelection && !showOverlay ? "w-5" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleSelect(audio.id, audio);
                          }}
                          className={`w-5 h-5 rounded flex items-center justify-center transition-all shadow-sm ${
                            isSelected
                              ? "bg-primary-500 border-none text-white"
                              : "bg-white dark:bg-gray-700 border border-slate-300 dark:border-gray-500 hover:border-primary-400"
                          }`}
                        >
                          {isSelected && <FiCheck className="h-3 w-3" strokeWidth={3} />}
                        </button>
                      </div>
                      {/* 2. Play/Pause Button */}
                      <button
                        type="button"
                        onClick={(e) => togglePlayPause(e, audio.id)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm ${
                          isPlaying
                            ? "bg-primary-500 text-white shadow-primary-500/30"
                            : "bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-500 dark:text-gray-400 hover:border-primary-300 hover:text-primary-500 hover:shadow-md"
                        }`}
                        aria-label={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? (
                          <FiPause className="h-3 w-3" />
                        ) : (
                          <FiPlay className="h-3 w-3 ml-0.5" />
                        )}
                      </button>
                    </div>

                    {/* Info + progress */}
                    <div className="flex-1 min-w-0 flex flex-col gap-1 ml-0.5">
                      <p
                        className={`text-[12px] font-medium truncate leading-none ${
                          isSelected
                            ? "text-primary-700 dark:text-primary-300"
                            : "text-slate-700 dark:text-gray-200"
                        }`}
                      >
                        {audio.filename}
                      </p>

                      {/* Progress bar */}
                      <div
                        className="h-1.5 w-full bg-slate-200 dark:bg-gray-700 rounded-full cursor-pointer group/progress relative"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          isScrubbing.current = true;
                          const handleUp = () => {
                            setTimeout(() => {
                              isScrubbing.current = false;
                            }, 50);
                            window.removeEventListener("pointerup", handleUp);
                          };
                          window.addEventListener("pointerup", handleUp);
                        }}
                      >
                        <div className="absolute inset-x-0 h-1.5 bg-slate-200 dark:bg-gray-700 rounded-full pointer-events-none" />
                        <div
                          className="absolute inset-y-0 left-0 h-1.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full pointer-events-none"
                          style={{ width: `${percent}%` }}
                        />
                        <div
                          className="absolute w-2.5 h-2.5 bg-white border border-primary-400 rounded-full shadow-sm opacity-0 group-hover/progress:opacity-100 pointer-events-none z-10"
                          style={{ left: `calc(${percent}% - 5px)` }}
                        />
                        <input
                          type="range"
                          min={0}
                          max={times.duration || 100}
                          value={times.current || 0}
                          step="any" // "any" ensures buttery smooth sub-second scrubbing
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20 m-0"
                          onChange={(e) => {
                            const audioEl = audioRefs.current.get(audio.id);
                            if (audioEl) {
                              audioEl.currentTime = Number(e.target.value);
                            }
                          }}
                        />
                      </div>

                      {times.duration > 0 && (
                        <p className="text-[10px] text-slate-400 dark:text-gray-500 leading-none">
                          {Math.floor(times.current)}s / {Math.floor(times.duration)}s
                        </p>
                      )}
                    </div>

                    {/* Three-dot menu */}
                    {!isSelected && (
                      <div
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ThreeDotMenu
                          media={audio}
                          onProperties={() => handleProperties(audio)}
                          onMove={() => handleMove([audio])}
                          onDelete={() => handleDelete([audio])}
                        />
                      </div>
                    )}

                    {/* Copying overlay */}
                    {copying && (
                      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center rounded-xl z-20">
                        <div className="w-5 h-5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                      </div>
                    )}
                  </div>
                </Dnd>
              );
            })
          )}

          {hasMore && (
            <div ref={observerTarget} className="h-8 flex items-center justify-center">
              {loading && (
                <div className="w-4 h-4 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
              )}
            </div>
          )}
        </div>
      </div>

      <FloatingActionBar
        selectedItems={selectedMediaArray}
        selectedCount={selectedMedia.size}
        mediaType="audio"
        onClearSelection={handleClearSelection}
        onRename={handleRename}
        onMove={handleMove}
        onDelete={handleDelete}
      />

      {activeMedia && (
        <>
          <RenameMediaModal
            isOpen={showRenameModal}
            onClose={() => {
              setShowRenameModal(false);
              setActiveMedia(null);
            }}
            mediaId={activeMedia.id}
            currentFilename={activeMedia.filename}
            mediaType="audio"
          />
          <MediaPropertiesDialog
            isOpen={showPropertiesDialog}
            onClose={() => {
              setShowPropertiesDialog(false);
              setActiveMedia(null);
            }}
            media={activeMedia}
            mediaType="audio"
          />
        </>
      )}

      <MoveMediaModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setItemsToMove([]);
        }}
        mediaIds={itemsToMove.map((m) => m.id)}
        mediaType="audio"
        currentFolderId={currentFolderId}
      />

      <TextToSpeechModal
        isOpen={showTextToSpeechModal}
        onClose={() => setShowTextToSpeechModal(false)}
        currentFolderId={currentFolderId}
      />

      <MediaUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        mediaType="audio"
        folderId={currentFolderId}
        refetchQuery="GetMediaHouseAudio"
      />

      {disabled && (
        <DisabledLockOverlay
          title="Audio Not Available"
          description="This slide type doesn't support audio elements"
        />
      )}
    </div>
  );
}
